import { useEffect, useRef, useState } from 'react';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import { IconButton, TextField } from '@radix-ui/themes';
import './PropertyInput.css';

interface PropertyInputProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

function clampValue(parsed: number, min?: number, max?: number): number {
  let result = parsed;
  if (min !== undefined) {
    result = Math.max(result, min);
  }
  if (max !== undefined) {
    result = Math.min(result, max);
  }
  return result;
}

function parseCommittedValue(text: string, step: number): number | null {
  const parsed = Number.parseFloat(text);
  if (Number.isNaN(parsed)) {
    return null;
  }

  if (Number.isInteger(step)) {
    return Math.round(parsed);
  }

  const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0;
  return Number(parsed.toFixed(decimals));
}

export function PropertyInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: PropertyInputProps) {
  const [text, setText] = useState(String(value));
  const isFocusedRef = useRef(false);
  const accessibleName = ariaLabel ?? label ?? 'Value';

  useEffect(() => {
    if (!isFocusedRef.current) {
      setText(String(value));
    }
  }, [value]);

  const applyValue = (next: number) => {
    const clampedValue = clampValue(next, min, max);
    setText(String(clampedValue));
    if (clampedValue !== value) {
      onChange(clampedValue);
    }
  };

  const commit = () => {
    const parsedValue = parseCommittedValue(text, step);
    if (parsedValue === null) {
      setText(String(value));
      return;
    }
    applyValue(parsedValue);
  };

  const nudge = (direction: 1 | -1) => {
    if (disabled) return;
    applyValue(value + direction * step);
  };

  const canDecrement = min === undefined || value > min;
  const canIncrement = max === undefined || value < max;

  return (
    <div className={`property-input-group${className ? ` ${className}` : ''}`}>
      {label ? <label className="property-label">{label}</label> : null}
      <TextField.Root
        size="1"
        variant="surface"
        radius="medium"
        className="property-number-field"
        type="text"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        aria-label={accessibleName}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
            return;
          }
          if (e.key === 'Escape') {
            setText(String(value));
            e.currentTarget.blur();
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            nudge(1);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            nudge(-1);
          }
        }}
      >
        <TextField.Slot side="right" px="1" gap="0">
          <div className="property-number-steppers">
            <IconButton
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              highContrast
              className="property-number-step"
              aria-label={`Increase ${accessibleName}`}
              disabled={disabled || !canIncrement}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => nudge(1)}
            >
              <CaretUpIcon />
            </IconButton>
            <IconButton
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              highContrast
              className="property-number-step"
              aria-label={`Decrease ${accessibleName}`}
              disabled={disabled || !canDecrement}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => nudge(-1)}
            >
              <CaretDownIcon />
            </IconButton>
          </div>
        </TextField.Slot>
      </TextField.Root>
    </div>
  );
}
